import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Pencil, Trash2, CreditCard, Lock, Clock4 } from 'lucide-react';
import {
  listMyPaymentMethods, createMyPaymentMethod,
  updateMyPaymentMethod, archiveMyPaymentMethod,
} from '@/services/realtimeApi';
import { METHOD_BY_KEY, STATUS_BY_KEY, WEEKDAYS } from '@/components/payment-methods/methodConfig';
import PaymentMethodForm from '@/components/payment-methods/PaymentMethodForm';

const ScheduleRow = ({ schedule }) => {
  const entries = WEEKDAYS.filter(([k]) => schedule?.[k]);
  if (entries.length === 0) return <span className="text-xs text-slate-400">Siempre disponible</span>;
  return (
    <div className="text-[11px] text-slate-600 leading-relaxed">
      {entries.map(([k, label]) => (
        <span key={k} className="mr-2"><b>{label}:</b> {schedule[k]}</span>
      ))}
    </div>
  );
};

const ProviderPaymentMethodsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listMyPaymentMethods(getAuthHeaders);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar métodos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSubmit = async (payload) => {
    if (editing) {
      await updateMyPaymentMethod(getAuthHeaders, editing.id, payload);
      toast.success('Método actualizado');
    } else {
      await createMyPaymentMethod(getAuthHeaders, payload);
      toast.success('Método creado');
    }
    setEditing(null);
    setShowForm(false);
    await load();
  };

  const handleArchive = async (m) => {
    if (!window.confirm(`¿Archivar el método "${m.label}"? Se ocultará del catálogo y de los gestores.`)) return;
    try {
      await archiveMyPaymentMethod(getAuthHeaders, m.id);
      toast.success('Método archivado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al archivar');
    }
  };

  return (
    <div className="space-y-6" data-testid="provider-payment-methods-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-600" />
            Métodos de Pago
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Configura cómo los gestores te envían fondos. Cada método tiene instrucciones, límites, monedas y horarios.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="pm-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="pm-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo método
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="border-0 shadow-sm p-12 text-center text-slate-500" data-testid="pm-empty">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="font-medium">Aún no hay métodos configurados</p>
          <p className="text-xs text-slate-400 mt-1">Crea tu primer método para que los gestores puedan enviarte fondos.</p>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="pm-empty-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Crear primer método
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((m) => {
            const t = METHOD_BY_KEY[m.type] || { label: m.type, icon: CreditCard };
            const s = STATUS_BY_KEY[m.status] || { label: m.status, color: 'bg-slate-100 text-slate-700' };
            const Ic = t.icon;
            return (
              <Card key={m.id} className="border-0 shadow-sm p-4 space-y-3" data-testid={`pm-card-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2 bg-blue-50 rounded-lg shrink-0"><Ic className="w-5 h-5 text-blue-700" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{m.label}</p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">{t.label} · {m.country}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] font-bold border ${s.color}`} data-testid={`pm-status-${m.id}`}>{s.label}</Badge>
                </div>

                {m.instructions && (
                  <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded border whitespace-pre-line line-clamp-3">{m.instructions}</p>
                )}

                <div className="flex flex-wrap gap-1">
                  {(m.currencies || []).map((c) => (
                    <span key={c} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono text-slate-700">{c}</span>
                  ))}
                </div>

                {m.limits && (
                  <div className="text-[11px] text-slate-600 flex flex-wrap gap-3 border-t pt-2">
                    {m.limits.min_amount != null && <span>Mín: <b>${m.limits.min_amount}</b></span>}
                    {m.limits.max_amount != null && <span>Máx: <b>${m.limits.max_amount}</b></span>}
                    {m.limits.daily_cap  != null && <span>Tope diario: <b>${m.limits.daily_cap}</b></span>}
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-slate-500 border-t pt-2">
                  <Clock4 className="w-3 h-3 shrink-0" />
                  <ScheduleRow schedule={m.schedule} />
                </div>

                <div className="flex items-center justify-end gap-2 border-t pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(m); setShowForm(true); }} data-testid={`pm-edit-${m.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleArchive(m)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`pm-archive-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Archivar
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-slate-400 italic flex items-center gap-1">
        <Lock className="w-3 h-3" /> Los gestores ven instrucciones y límites, pero nunca tus datos sensibles (cuentas, wallets, direcciones).
      </p>

      <PaymentMethodForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSubmit={handleSubmit}
        initial={editing}
      />
    </div>
  );
};

export default ProviderPaymentMethodsPage;
